import React from 'react';
import { ThemeProvider } from 'styled-components';
import './fonts.css';
import './style.css';

export const Device = {
    Laptop: '1024px',
    Desktop: '2560px',
};

export type ColorVariants =
    | 'primary'
    | 'action'
    | 'inverted'
    | 'neutral'
    | 'selected'
    | 'disabled'
    | 'contrast'
    | 'danger'
    | 'clear'
    | 'hollow';

export type ColorMode = 'dark' | 'light';

const colors = {
    primary: 'black',
    inverted: 'white',
    accent: '#F96621', // Orbital Orange (original was #e67c24)
    accentTrans: '#F9662142', // Orbital Orange
    accent2: '#799FF7', // Lightsaber Blue
    highlight: 'rgba(0, 194, 255, 0.26)',
    lightGray: '#EFEEEB', // Aluminium
    darkAccent: '#f96621',
    red: '#EA0000',

    yellow50: '#FBC414', // Mustard

    orange950: '#E9431A',
    orange900: '#f96621',
    orange500: '#F38A59',
    orange100: '#ffcbb2',
    orange50: '#F96621',

    green900: '#017c0a',
    green800: '#298A00',
    green800a: '#29FF382E',
    green500: '#00D916',
    green100: '#9df3a4',

    black900: '#282828',
    black800: '#3B3B3B',
    black600: '#626262', // Titanium
    black500: '#7E7E7E',
    black400: '#9E9E9E',
    black300: '#B1B1B1',
    black200: '#D4D2CF',
    black50: '#F7F7F7',

    cyan500: '#00FFFF',
    cyan1a: '#00FFFF55',

    blue1: '#5463ff',
    blue900: '#1C1F24',
    blue700: '#334E68',
    blue600: '#486581',
    blue400: '#829AB1',
    blue50: '#F0F4F8',

    gray1: '#3B3B3B',
    gray2: '#515151',
    gray0a: 'rgba(0,0,0,0.75)',
    gray1a: 'rgba(28,28,28,0.9)',
    gray1ab: 'rgba(40,40,40,0.57)',
    gray2a: 'rgba(0,0,0,0.25)',
    gray2ab: 'rgba(64,64,64,0.5)',
    gray3a: 'rgba(128,128,128,0.5)',
    gray3ab: 'rgba(98,98,98,0.22)',
    gray4a: 'rgba(128,128,128,0.1)',

    gray110: '#303030',
    gray100: '#484848',
    gray80: '#6b6b6b',
    gray75: '#808080',
    gray50: '#c9c9c9',
    gray40: '#d6d6d6',
    gray25: '#e4e4e4',
    gray10: '#f1f1f1',

    bluegray300: '#424347',

    white0a: 'rgba(255, 255, 255, 0.9)',
    white1a: 'rgba(255, 255, 255, 0.5)',
    white2a: 'rgba(255, 255, 255, 0.25)',
    white1: '#f3f3f3',

    whiteAccentBackground: 'rgb(240, 240, 240)',

    white: '#fff',
    black: '#000',
    error: '#a81313',
};

const defaultTheme = {
    chipSpacing: '10px',
    colors: {} as { [id: string]: any }, // Populated in GetTheme()
    edgeContentPadding: 15,
    navItemPadding: 8,
    navIconSize: 1.75,
    largeIconSize: 2.75,
    font: 'Oxygen',
    boxShadow: `0px 2px 6px ${colors.gray3a}`,
    monospaceFont: 'Roboto Mono',
    input: {
        borderColor: colors.black,
        errorBorderColor: colors.error,
        padding: '8px',
    },
    margin: {
        sm: '4px',
        md: '8px',
        lg: '12px',
    },
    padding: {
        sm: '4px',
        md: '8px',
        lg: '12px',
    },
    text: {
        color: colors.primary,
        size: '0.75rem',
        Heading: {
            as: ['h5', 'h4', 'h3', 'h2', 'h1'],
            size: ['0.938rem', '1.125rem', '1.313rem', '1.5rem', '2.25rem'],
            weight: 'bold',
            props: {},
        },
        Subtitle: {
            size: ['0.75rem', '0.75rem', '0.875rem', '1rem', '2.25rem'],
            as: 'div',
            weight: '300',
            props: {},
        },
        Text: {
            size: ['0.75rem', '0.875rem', '0.875rem', '1rem', '2.25rem'],
            as: 'div',
            weight: 'normal',
            props: {},
        },
        Caption: {
            size: ['0.625rem', '0.625rem', '0.625rem', '0.75rem', '0.75rem'],
            as: 'div',
            weight: ['bold', 'bold', 'bold', 'bold', '300'],
            props: {},
        },
        Overline: {
            size: ['0.625rem', '0.625rem', '0.625rem', '0.625rem', '0.75rem'],
            as: 'div',
            weight: 'bold',
            props: {},
        },
        Hyperlink: {
            size: ['0.75rem', '0.75rem', '0.875rem', '1rem', '2.25rem'],
            as: 'div',
            weight: 'bold',
            props: { textDecoration: 'underline' },
        },
    },
    status: {
        none: '',
        loading: 'rgba(0,0,0,.15)',
        error: colors.red,
        success: colors.accent,
    },
};

const lightColors = {
    colors: {
        ...colors,
        primary: {
            bg: colors.gray4a,
            fg: colors.black800,
            invertedBg: colors.black,
            invertedFg: colors.white,
            accent: colors.accent,
            subtleAccent: colors.blue50,
            invertAccent: colors.accent2,
            border: colors.gray3a,
            borderAlt: 'transparent',
            hoverBorder: colors.accent,
            highlight: colors.white,
            transBg: colors.white0a,
            altTransBg: colors.gray0a,
            halfTransBg: colors.white2a,
            neutralBg: colors.gray2a,
            buttonBorder: 'transparent',
            buttonHoverBorder: 'transparent',
        },
        inverted: {
            bg: colors.black,
            fg: colors.white,
            fgAlt: colors.white,
            invertedBg: colors.white,
            invertedFg: colors.black300,
            accent: colors.accent,
            border: colors.inverted,
            hoverBorder: colors.inverted,
            highlight: colors.highlight,
            buttonBorder: 'transparent',
            buttonHoverBorder: 'transparent',
        },
        neutral: {
            bg: colors.gray75,
            fg: colors.inverted,
            fgAlt: colors.inverted,
            invertedBg: colors.gray100,
            invertedFg: colors.inverted,
            accent: colors.accent,
            border: colors.primary,
            hoverBorder: colors.inverted,
            highlight: colors.highlight,
            buttonBorder: 'transparent',
            buttonHoverBorder: 'transparent',
        },
        hovered: {
            bg: colors.primary,
            fg: colors.inverted,
            fgAlt: colors.inverted,
            invertedBg: colors.inverted,
            invertedFg: colors.primary,
            accent: colors.primary,
            border: colors.primary,
            hoverBorder: colors.inverted,
            highlight: colors.highlight,
            buttonBorder: 'transparent',
            buttonHoverBorder: 'transparent',
        },
        selected: {
            bg: colors.black900,
            fg: colors.orange50,
            fgAlt: colors.orange50,
            invertedBg: colors.white,
            invertedFg: colors.orange50,
            accent: colors.white,
            border: colors.inverted,
            hoverBorder: colors.inverted,
            highlight: colors.highlight,
            buttonBorder: 'transparent',
            buttonHoverBorder: 'transparent',
        },
        action: {
            bg: colors.highlight,
            fg: colors.black800,
            fgAlt: colors.inverted,
            invertedBg: colors.accent2,
            invertedFg: colors.white,
            accent: colors.accent,
            border: colors.accent,
            hoverBorder: colors.accent,
            highlight: colors.highlight,
            buttonBorder: 'transparent',
            buttonHoverBorder: 'transparent',
        },
        disabled: {
            bg: 'rgba(0,0,0,.15)',
            fg: colors.black400,
            fgAlt: colors.black400,
            invertedBg: colors.black500,
            invertedFg: colors.black300,
            accent: colors.gray50,
            border: 'rgba(0,0,0,.15)',
            hoverBorder: 'rgba(0,0,0,.15)',
            highlight: colors.highlight,
            buttonBorder: 'transparent',
            buttonHoverBorder: 'transparent',
        },
        contrast: {
            bg: colors.white,
            fg: colors.black,
            fgAlt: colors.black,
            invertedBg: colors.black,
            invertedFg: colors.white,
            accent: colors.accent,
            border: colors.white,
            hoverBorder: colors.gray80,
            highlight: colors.highlight,
            buttonBorder: 'transparent',
            buttonHoverBorder: 'transparent',
        },
        danger: {
            bg: colors.inverted,
            fg: colors.error,
            fgAlt: colors.error,
            invertedBg: colors.error,
            invertedFg: colors.inverted,
            accent: colors.accent,
            border: colors.error,
            hoverBorder: colors.error,
            highlight: colors.highlight,
            buttonBorder: 'transparent',
            buttonHoverBorder: 'transparent',
        },
        clear: {
            bg: 'transparent',
            fg: colors.white,
            invertedBg: colors.gray2,
            invertedFg: colors.white,
            accent: colors.accent,
            border: colors.gray75,
            hoverBorder: colors.primary,
            highlight: colors.highlight,
            buttonBorder: 'transparent',
            buttonHoverBorder: 'transparent',
        },
    },
};

const darkColors = {
    colors: {
        ...colors,
        primary: {
            bg: colors.black900,
            fg: colors.white,
            invertedBg: colors.white,
            invertedFg: colors.black,
            accent: colors.accent,
            invertAccent: colors.accent2,
            subtleAccent: colors.blue900,
            border: colors.gray3a,
            borderAlt: colors.gray110,
            hoverBorder: colors.primary,
            highlight: colors.highlight,
            altTransBg: colors.white2a,
            transBg: colors.gray1a,
            halfTransBg: colors.gray2a,
            buttonBorder: colors.black200,
            buttonHoverBorder: colors.black200,
        },
        inverted: {
            bg: colors.black,
            fg: colors.white,
            fgAlt: colors.white,
            invertedBg: colors.white,
            invertedFg: colors.black300,
            accent: colors.accent,
            border: colors.inverted,
            hoverBorder: colors.inverted,
            highlight: colors.highlight,
            buttonBorder: 'transparent',
            buttonHoverBorder: 'transparent',
        },
        neutral: {
            bg: colors.lightGray,
            fg: colors.black600,
            fgAlt: colors.inverted,
            transBg: colors.lightGray,
            invertedBg: colors.gray100,
            invertedFg: colors.inverted,
            accent: colors.accent,
            border: colors.primary,
            hoverBorder: colors.inverted,
            highlight: colors.highlight,
            buttonBorder: 'transparent',
            buttonHoverBorder: 'transparent',
        },
        hovered: {
            bg: colors.primary,
            fg: colors.inverted,
            fgAlt: colors.inverted,
            invertedBg: colors.inverted,
            invertedFg: colors.primary,
            accent: colors.primary,
            border: colors.primary,
            hoverBorder: colors.inverted,
            highlight: colors.highlight,
            buttonBorder: 'transparent',
            buttonHoverBorder: 'transparent',
        },
        selected: {
            bg: colors.black900,
            fg: colors.orange50,
            fgAlt: colors.orange50,
            invertedBg: colors.white,
            invertedFg: colors.orange50,
            accent: colors.white,
            border: colors.inverted,
            hoverBorder: colors.inverted,
            highlight: colors.highlight,
            buttonBorder: 'transparent',
            buttonHoverBorder: 'transparent',
        },
        action: {
            bg: colors.accent,
            fg: colors.inverted,
            fgAlt: colors.inverted,
            invertedBg: colors.orange100,
            invertedFg: colors.black,
            accent: colors.accent,
            border: colors.accent,
            hoverBorder: colors.accent,
            highlight: colors.highlight,
            buttonBorder: 'transparent',
            buttonHoverBorder: 'transparent',
        },
        disabled: {
            bg: 'rgba(0,0,0,.15)',
            fg: colors.black400,
            fgAlt: colors.black400,
            invertedBg: colors.black500,
            invertedFg: colors.black300,
            accent: colors.gray50,
            border: 'rgba(0,0,0,.15)',
            hoverBorder: 'rgba(0,0,0,.15)',
            highlight: colors.highlight,
            buttonBorder: 'transparent',
            buttonHoverBorder: 'transparent',
        },
        contrast: {
            bg: colors.white,
            fg: colors.black,
            fgAlt: colors.black,
            invertedBg: colors.black,
            invertedFg: colors.white,
            accent: colors.accent,
            border: colors.gray75,
            hoverBorder: colors.gray80,
            highlight: colors.highlight,
            buttonBorder: 'transparent',
            buttonHoverBorder: 'transparent',
        },
        danger: {
            bg: colors.inverted,
            fg: colors.error,
            fgAlt: colors.error,
            invertedBg: colors.error,
            invertedFg: colors.inverted,
            accent: colors.accent,
            border: colors.error,
            hoverBorder: colors.error,
            highlight: colors.highlight,
            buttonBorder: 'transparent',
            buttonHoverBorder: 'transparent',
        },
        clear: {
            bg: 'transparent',
            fg: colors.white,
            invertedBg: colors.gray2,
            invertedFg: colors.white,
            accent: colors.accent,
            border: colors.gray75,
            hoverBorder: colors.primary,
            highlight: colors.highlight,
            buttonBorder: 'transparent',
            buttonHoverBorder: 'transparent',
        },
    },
};

const GetTheme = (mode: ColorMode = 'light') => {
    const colorMap = {
        light: lightColors,
        dark: darkColors,
    };

    return {
        ...defaultTheme,
        ...colorMap[mode],
    };
};

const ThemeModeStorageKey = 'themeMode';

const DefaultThemeProvider = ({ children }: any) => {
    const themeMode = localStorage.getItem(ThemeModeStorageKey) as ColorMode;
    return (
        <ThemeProvider theme={GetTheme(themeMode ?? 'dark')}>
            {children}

            {/* Some components in this library use portals to create stuff in the top-most level of the page.
            This div can be an effective target for such functionality. */}
            <div id="portal-me"></div>
        </ThemeProvider>
    );
};

type OSKThemeType = typeof defaultTheme;
const theme = defaultTheme;

export type { OSKThemeType };
export { DefaultThemeProvider, theme, ThemeModeStorageKey };
